import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Cake as CakeIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";
import TransactionDetailsModal from "./TransactionDetailsModal";
import LedgerModal from "./LedgerModal";
import { DataContext } from "../../context/DataContext";
import { useNavigate } from "react-router-dom";

const CustomerActionModal = ({
  isOpen,
  onClose,
  customer,
  isDarkMode = false,
}) => {
  const theme = useTheme();
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const { customerTransactions } = React.useContext(DataContext);
  const navigate = useNavigate(); // Add this line


  const handleViewTransactions = () => {
    setTransactionModalOpen(true);
  };
  const fetchCustomerTransactions = async (customerId) => {
    console.log("Fetching transactions for customer ID:", customerTransactions);
    let filteredTransactions = customerTransactions?.filter((transaction) => {
      return transaction.customer === customerId;
    })
    console.log(filteredTransactions);
    return filteredTransactions; // placeholder
  };

  const handleViewLedger = () => {
    setLedgerModalOpen(true);
  };


  if (!isOpen || !customer) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          background: isDarkMode
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha(theme.palette.background.paper, 0.98),
          backdropFilter: "blur(20px)",
          border: isDarkMode
            ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
            : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: theme.shadows[24],
          overflow: "hidden",
          minHeight: 480,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          // pb: 2,
          height: "80px",
          background: isDarkMode
            ? `linear-gradient(120deg, ${alpha(
              theme.palette.primary.dark,
              0.3
            )}, ${alpha(theme.palette.secondary.dark, 0.3)})`
            : `linear-gradient(120deg, ${alpha(
              theme.palette.primary.light,
              0.1
            )}, ${alpha(theme.palette.secondary.light, 0.1)})`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountCircleIcon
                sx={{
                  fontSize: 28,
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                Customer Actions
                <Typography
                  component="span"
                  sx={{
                    color: theme.palette.primary.main,
                    ml: 1,
                    fontSize: "25px",
                    fontWeight: 800,
                  }}
                >
                  - {customer.name}
                </Typography>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              ></Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 2, mt: 3 }}>
        <Grid container spacing={2}>
          {/* Transaction History Card */}
          <Grid item size={{ xs: 12, md: 12 }} sx={{ mb: 1, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  marginTop: '10px',
                  height: "100%",
                  borderRadius: 2,
                  width: "400px",
                  background: isDarkMode
                    ? alpha(theme.palette.primary.dark, 0.1)
                    : alpha(theme.palette.primary.light, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0 8px 24px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <ReceiptIcon
                      sx={{
                        mr: 1.5,
                        color: theme.palette.primary.main,
                        fontSize: 24,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      Transaction History
                    </Typography>
                  </Box>

                  <Stack spacing={1.5}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ArrowForwardIcon />}
                      onClick={handleViewTransactions}
                      sx={{
                        justifyContent: "flex-start",
                        py: 1.5,
                        borderRadius: 1.5,
                        borderColor: theme.palette.primary.main,
                        color: isDarkMode
                          ? "#FFFFFF"
                          : theme.palette.primary.dark,
                        backgroundColor: isDarkMode
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.primary.main, 0.05),
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        letterSpacing: 0.3,
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: isDarkMode
                            ? alpha(theme.palette.primary.main, 0.25)
                            : alpha(theme.palette.primary.main, 0.1),
                          transform: "translateY(-2px)",
                          boxShadow: `0 6px 16px ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                        },
                      }}
                    >
                      View All Transactions
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        navigate('/create-customer-transactions');
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        py: 1.5,
                        borderRadius: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: "#ffffff",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        boxShadow: `0 4px 12px ${alpha(
                          theme.palette.primary.main,
                          0.3
                        )}`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
                          boxShadow: `0 6px 20px ${alpha(
                            theme.palette.primary.main,
                            0.4
                          )}`,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      Add New Transaction
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Ledger Management Card */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  marginTop: '10px',
                  height: "100%",
                  borderRadius: 2,
                  width: "400px",
                  background: isDarkMode
                    ? alpha("#10B981", 0.1)
                    : alpha("#10B981", 0.05),
                  border: `1px solid ${alpha("#10B981", 0.2)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0 8px 24px ${alpha("#10B981", 0.15)}`,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AccountBalanceIcon
                      sx={{
                        mr: 1.5,
                        color: "#10B981",
                        fontSize: 24,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#10B981",
                      }}
                    >
                      Ledger Management
                    </Typography>
                  </Box>

                  <Stack spacing={1.5}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<HistoryIcon />}
                      onClick={handleViewLedger}
                      sx={{
                        justifyContent: "flex-start",
                        py: 1.5,
                        borderRadius: 1.5,
                        borderColor: alpha("#10B981", 0.4),
                        color: isDarkMode ? "#86EFAC" : "#065F46", // Light green for dark, dark green for light
                        backgroundColor: isDarkMode
                          ? alpha("#10B981", 0.15)
                          : alpha("#10B981", 0.08),
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        letterSpacing: 0.2,
                        "&:hover": {
                          borderColor: "#10B981",
                          backgroundColor: isDarkMode
                            ? alpha("#10B981", 0.25)
                            : alpha("#10B981", 0.15),
                          transform: "translateY(-2px)",
                          boxShadow: `0 6px 16px ${alpha("#10B981", 0.25)}`,
                        },
                      }}
                    >
                      View Ledger Balance
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        justifyContent: "flex-start",
                        py: 1.5,
                        borderRadius: 1.5,
                        background: "linear-gradient(135deg, #10B981, #059669)",
                        color: "#ffffff",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        boxShadow: `0 4px 12px ${alpha("#10B981", 0.3)}`,
                        "&:hover": {
                          background: "linear-gradient(135deg, #059669, #047857)",
                          boxShadow: `0 6px 20px ${alpha("#10B981", 0.4)}`,
                          transform: "translateY(-1px)",
                        },
                      }}
                      onClick={() => {
                        navigate('/create-customer-transactions');
                      }}
                    >
                      Add Ledger Entry
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {/* Customer Details Card */}
<Grid item size={{ xs: 12, md: 12 }}>
  <Card
    elevation={0}
    sx={{
      borderRadius: 2,
      justifyContent: "space-evenly",
      background: isDarkMode
        ? alpha(theme.palette.background.paper, 0.6)
        : alpha(theme.palette.background.paper, 0.8),
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2.5,
          fontWeight: 600,
          color: theme.palette.text.primary,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        Customer Details
      </Typography>

      <Grid container spacing={2.5}>
        {/* Contact */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  0.1
                ),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PhoneIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 18,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                Contact
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {customer.contact || "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Address */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha("#8B5CF6", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocationOnIcon
                sx={{
                  color: "#8B5CF6",
                  fontSize: 18,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                Address
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {customer.address || "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* PAN Card Number */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha("#FF6B6B", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReceiptIcon
                sx={{
                  color: "#FF6B6B",
                  fontSize: 18,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                PAN Card
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontFamily: "monospace",
                  letterSpacing: "0.5px",
                }}
              >
                {customer.panNumber || "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* GST Number */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha("#4A90E2", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AccountBalanceIcon
                sx={{
                  color: "#4A90E2",
                  fontSize: 18,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                GST Number
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontFamily: "monospace",
                  letterSpacing: "0.5px",
                }}
              >
                {customer.gstNumber || "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Date of Birth */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha("#F59E0B", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CakeIcon
                sx={{
                  color: "#F59E0B",
                  fontSize: 18,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                Date of Birth
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {customer.dob ? customer.dob.replace(/^'/, '') : "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Anniversary */}
        <Grid item xs={12} sm={6} md={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: alpha("#EC4899", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FavoriteIcon
                sx={{
                  color: "#EC4899",
                  fontSize: 18,
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: "block",
                }}
              >
                Anniversary
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {customer.anniversary ? customer.anniversary.replace(/^'/, '') : "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
</Grid>

          {/* Quick Actions */}
          {/* TODO */}
          {/* <Grid item size={{ xs: 12, md: 12 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 1,
                background: isDarkMode
                  ? alpha(theme.palette.background.paper, 0.6)
                  : alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SmsIcon />}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: `0 4px 12px ${alpha(
                          theme.palette.primary.main,
                          0.3
                        )}`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
                          boxShadow: `0 6px 16px ${alpha(
                            theme.palette.primary.main,
                            0.4
                          )}`,
                        },
                      }}
                    >
                      Send SMS
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AssessmentIcon />}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        background: "linear-gradient(135deg, #10B981, #059669)",
                        boxShadow: `0 4px 12px ${alpha("#10B981", 0.3)}`,
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #059669, #047857)",
                          boxShadow: `0 6px 16px ${alpha("#10B981", 0.4)}`,
                        },
                      }}
                    >
                      Generate Report
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
                        boxShadow: `0 4px 12px ${alpha("#8B5CF6", 0.3)}`,
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #7C3AED, #6D28D9)",
                          boxShadow: `0 6px 16px ${alpha("#8B5CF6", 0.4)}`,
                        },
                      }}
                    >
                      Export Data
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<NotificationsIcon />}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        background: "linear-gradient(135deg, #F59E0B, #D97706)",
                        boxShadow: `0 4px 12px ${alpha("#F59E0B", 0.3)}`,
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #D97706, #B45309)",
                          boxShadow: `0 6px 16px ${alpha("#F59E0B", 0.4)}`,
                        },
                      }}
                    >
                      Schedule Reminder
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>
      </DialogContent>

      <TransactionDetailsModal
        isOpen={transactionModalOpen}
        onClose={() => setTransactionModalOpen(false)}
        customer={customer}
        fetchCustomerTransactions={fetchCustomerTransactions}
      />
      <LedgerModal
        isOpen={ledgerModalOpen}
        onClose={() => setLedgerModalOpen(false)}
        customer={customer}
        fetchCustomerTransactions={fetchCustomerTransactions}
      />

    </Dialog>
  );
};

export default CustomerActionModal;
